<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { api } from '$lib/api/client';
  import type { ImageCardData } from '$lib/api/types';

  export let data: ImageCardData;
  export let editable = false;
  export let hovered = false;

  const dispatch = createEventDispatcher<{
    change: ImageCardData;
  }>();

  let uploading = false;
  let fileInput: HTMLInputElement;

  function handleClick() {
    if (editable && !data.imageUrl) {
      fileInput.click();
    } else if (data.linkUrl) {
      window.open(data.linkUrl, '_blank', 'noopener,noreferrer');
    }
  }

  async function handleFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    uploading = true;

    try {
      const result = await api.upload.image(file, 'card');
      dispatch('change', { ...data, imageUrl: result.url });
    } catch (error) {
      console.error('Failed to upload image:', error);
    } finally {
      uploading = false;
      input.value = '';
    }
  }

  function handleRemove(event: MouseEvent) {
    event.stopPropagation();
    if (confirm('画像を削除しますか？')) {
      dispatch('change', { ...data, imageUrl: '' });
    }
  }
</script>

<div
  class="h-full relative overflow-hidden cursor-pointer"
  on:click={handleClick}
  role={data.linkUrl || editable ? 'button' : undefined}
  tabindex={data.linkUrl || editable ? 0 : undefined}
>
  {#if data.imageUrl}
    <img
      src={data.imageUrl}
      alt={data.alt}
      class="w-full h-full {data.displayMode === 'contain' ? 'object-contain' : 'object-cover'}"
    />

    {#if data.caption}
      <div class="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-2 text-sm">
        {data.caption}
      </div>
    {/if}

    {#if editable && hovered}
      <button
        on:click={handleRemove}
        class="absolute top-2 right-2 z-30 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
      >
        <i class="lni lni-xmark text-xs"></i>
      </button>
    {/if}
  {:else}
    <div class="h-full flex flex-col items-center justify-center bg-gray-100 text-gray-400 relative z-30">
      {#if uploading}
        <div class="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-2"></div>
        <span class="text-sm">アップロード中...</span>
      {:else}
        <i class="lni lni-photos text-3xl mb-2"></i>
        {#if editable}
          <span class="text-sm">クリックして画像を追加</span>
        {:else}
          <span class="text-sm">画像なし</span>
        {/if}
      {/if}
    </div>
  {/if}

  <input
    bind:this={fileInput}
    type="file"
    accept="image/jpeg,image/png,image/webp,image/gif"
    class="hidden"
    on:change={handleFileChange}
  />
</div>
